import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useInView } from 'react-intersection-observer'

import { Layout, Header } from 'app/theme/layout'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'
import NavbarFixed from 'app/theme/NavbarFixed'
import ImageHero from 'app/theme/ImageHero'
import getGoogleMetaTags from './occasionSEOData'
import { OccasionPageGrid, OccasionPageGridRow } from '../occasions/shared/OccasionPageGrid'
import CuisineTypes from 'app/theme/sections/CuisineTypes'
import HowItWorks from 'app/theme/sections/HowItWorks'
import HowItWorksLarge from 'app/theme/sections/HowItWorksLarge'
import WhyWorkWithUs from 'app/theme/sections/WhyWorkWithUs'
import FeaturedClients from 'app/theme/sections/FeaturedClients'
import OccasionsSlider from 'app/theme/sections/CateringOccasionsSlider'

import heroImg from 'assets/img/office-lunch-catering-hero.jpg'
import gridImage1 from 'assets/img/office-lunch-catering-1.jpg'
import gridImage2 from 'assets/img/office-lunch-catering-2.jpg'
import gridImage3 from 'assets/img/office-lunch-catering-3.jpg'
import gridImage4 from 'assets/img/office-lunch-catering-4.jpg'

export default function OfficeLunchCateringPage() {
  const { openModal, ...modalState } = useModalState()

  const [ref, inView] = useInView({
    rootMargin: '1px',
    initialInView: true,
  })

  const googleMetaTags = getGoogleMetaTags('businessLunchFoodCourt')

  return (
    <Layout header={<Header occasionMenu />}>
      <Helmet>
        <title>Office Catering Programs - Roaming Hunger</title>
        <meta name="og:title" content="Office Catering Programs"></meta>
        <meta name="description" content="Create the best workplace perk with an office catering program. Daily, weekly or monthly, we make your office more delicious."></meta>
        <script type="application/ld+json">
          {googleMetaTags}
        </script>
      </Helmet>
      <LeadModal
        {...modalState}
      />
      <div ref={ref}>
        <ImageHero
          imageUrl={heroImg}
          title="Corporate Catering"
          onButtonClick={openModal}
          description={(
            <div>
              Engage employees, increase productivity and show everyone on the team how valued they are.
              Food trucks are an easy and inexpensive way to celebrate a company milestone or retain
              talent through an employee perk program.
            </div>
          )}
        />
      </div>
      {!inView && (
        <NavbarFixed
          onButtonClick={openModal}
        />
      )}

      <div className="container text-center py-12 px-8">
        <h2 className="text-navy text-4xl leading-tight font-platform">
          Make Your Office A Delicious Place to Work
        </h2>
        <p className="font-light text-gray-900 mt-6">
          Make everyday lunch a communal and special occasion, whether it's daily or monthly.
          With our office catering program, youâ€™ll have a flexible solution to increasing
          employee engagement and productivity. From 10 co-workers to 1,000, we curate the
          best blend of meals both inside your office with catering and outside your office with food trucks.
        </p>
      </div>

      <OccasionPageGrid className="bg-white py-8">
        <OccasionPageGridRow
          heading="Invest In Your Employees"
          content="Over 70% of employees prefer additional perks to a pay raise, which means implementing a lunch program can go straight to the bottom line. Make lunch catering a tool to improve retention, recruiting, and productivity."
          image={gridImage1}
          reverse
        />
        <OccasionPageGridRow
          heading="Make Everyone Happy"
          content="We'll collect your employees' food preferences to make sure we cater to your needs, including diet and allergy restrictions. That means your lunch gets better each time."
          image={gridImage2}
        />
      </OccasionPageGrid>

      <FeaturedClients
        onButtonClick={openModal}
      />

      <OccasionPageGrid className="bg-white py-8">
        <OccasionPageGridRow
          heading="All the Support You Need"
          content="Set up an office catering program so successful, you'll never need a platter of cold cuts again. One of our experienced catering agents will be your single point of contact and will support you through the entire program, making sure amazing meals become your office norm."
          image={gridImage3}
          reverse
        />
        <OccasionPageGridRow
          heading="Food Made With Love"
          content="What makes the food special? We work with local chefs that are as passionate about their food as we are about our customers. At the end of the day, food made with love just tastes better."
          image={gridImage4}
        />
      </OccasionPageGrid>

      <WhyWorkWithUs
        onButtonClick={openModal}
      />

      <CuisineTypes baseUrl='/food-truck-catering' />

      <HowItWorks
        heading="How Office Lunch Program Catering Works"
        onButtonClick={openModal}
      />
      <HowItWorksLarge
        heading="How Office Lunch Program Catering Works"
        onButtonClick={openModal}
      />

      <OccasionsSlider
        className="bg-white py-12"
      />
    </Layout>
  )
}
